import React, { useState, useEffect } from 'react';
import { Eye, Truck, Package } from 'lucide-react';
import api from '../../lib/api';
import { formatPrice, formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [trackingInfo, setTrackingInfo] = useState({
    status: '',
    shipping_carrier: '',
    tracking_number: '',
    tracking_url: ''
  });

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredOrders(orders);
    } else {
      setFilteredOrders(orders.filter(o => o.status === statusFilter));
    }
  }, [statusFilter, orders]);

  const fetchOrders = async () => {
    try {
      const response = await api.get('/admin/orders');
      setOrders(response.data);
      setFilteredOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setTrackingInfo({
      status: order.status,
      shipping_carrier: order.shipping_carrier || '',
      tracking_number: order.tracking_number || '',
      tracking_url: order.tracking_url || ''
    });
    setIsDialogOpen(true);
  };

  const handleUpdateOrder = async () => {
    try {
      await api.put(`/admin/orders/${selectedOrder.id}`, trackingInfo);
      toast.success('Order updated successfully');
      fetchOrders();
      setIsDialogOpen(false);
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      processing: 'bg-purple-100 text-purple-800',
      shipped: 'bg-green-100 text-green-800',
      delivered: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || styles.pending}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-serif text-4xl mb-2">Order Management</h1>
            <p className="text-gray-600">{filteredOrders.length} orders</p>
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Orders</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="shipped">Shipped</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Orders Table */}
        <div className="bg-white border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50" data-testid={`order-row-${order.id}`}>
                  <td className="px-6 py-4">
                    <div className="font-medium">{order.order_number}</div>
                    <div className="text-sm text-gray-500">{order.items.length} items</div>
                  </td>
                  <td className="px-6 py-4 text-sm">{order.shipping_address?.full_name || 'N/A'}</td>
                  <td className="px-6 py-4 text-sm">{formatDate(order.created_at)}</td>
                  <td className="px-6 py-4 font-medium">{formatPrice(order.total, order.currency)}</td>
                  <td className="px-6 py-4">{getStatusBadge(order.status)}</td>
                  <td className="px-6 py-4">
                    <span className={`text-xs ${order.payment_status === 'paid' ? 'text-green-600' : 'text-gray-600'}`}>
                      {order.payment_status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewOrder(order)}
                      data-testid={`view-order-${order.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Order Detail Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">
                Order {selectedOrder?.order_number}
              </DialogTitle>
            </DialogHeader>

            {selectedOrder && (
              <div className="space-y-6 mt-4">
                {/* Order Items */}
                <div>
                  <h3 className="font-medium mb-3">Order Items</h3>
                  <div className="space-y-2">
                    {selectedOrder.items.map((item, index) => (
                      <div key={index} className="flex justify-between py-2 border-b">
                        <div>
                          <p className="font-medium">{item.product_name}</p>
                          <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                        </div>
                        <p className="font-medium">{formatPrice(item.total_price, selectedOrder.currency)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 space-y-1">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>{formatPrice(selectedOrder.subtotal, selectedOrder.currency)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Shipping:</span>
                      <span>{formatPrice(selectedOrder.shipping_cost, selectedOrder.currency)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg pt-2 border-t">
                      <span>Total:</span>
                      <span>{formatPrice(selectedOrder.total, selectedOrder.currency)}</span>
                    </div>
                  </div>
                </div>

                {/* Shipping Address */}
                <div>
                  <h3 className="font-medium mb-3">Shipping Address</h3>
                  <div className="text-sm space-y-1">
                    <p>{selectedOrder.shipping_address?.full_name}</p>
                    <p>{selectedOrder.shipping_address?.line1}</p>
                    {selectedOrder.shipping_address?.line2 && <p>{selectedOrder.shipping_address.line2}</p>}
                    <p>
                      {selectedOrder.shipping_address?.city}, {selectedOrder.shipping_address?.state} {selectedOrder.shipping_address?.postcode}
                    </p>
                    <p>{selectedOrder.shipping_address?.country}</p>
                    <p>Phone: {selectedOrder.shipping_address?.phone}</p>
                  </div>
                </div>

                {/* Update Status & Tracking */}
                <div className="border-t pt-6">
                  <h3 className="font-medium mb-4">Update Order</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Order Status</Label>
                      <Select
                        value={trackingInfo.status}
                        onValueChange={(value) => setTrackingInfo({ ...trackingInfo, status: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="processing">Processing</SelectItem>
                          <SelectItem value="shipped">Shipped</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Shipping Carrier</Label>
                      <Input
                        value={trackingInfo.shipping_carrier}
                        onChange={(e) => setTrackingInfo({ ...trackingInfo, shipping_carrier: e.target.value })}
                        placeholder="e.g., FedEx, DHL"
                      />
                    </div>

                    <div>
                      <Label>Tracking Number</Label>
                      <Input
                        value={trackingInfo.tracking_number}
                        onChange={(e) => setTrackingInfo({ ...trackingInfo, tracking_number: e.target.value })}
                        placeholder="Enter tracking number"
                      />
                    </div>

                    <div>
                      <Label>Tracking URL</Label>
                      <Input
                        value={trackingInfo.tracking_url}
                        onChange={(e) => setTrackingInfo({ ...trackingInfo, tracking_url: e.target.value })}
                        placeholder="https://..."
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 mt-6">
                    <Button
                      onClick={handleUpdateOrder}
                      className="flex-1 bg-black text-white hover:bg-gray-900"
                      data-testid="update-order-button"
                    >
                      <Truck className="w-4 h-4 mr-2" />
                      Update Order
                    </Button>
                    <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminOrders;